import React, { useRef, useEffect, useState } from 'react';
import {
  Box, Typography, Avatar, IconButton, Tooltip, Menu, MenuItem,
  Dialog, DialogContent, DialogActions, DialogTitle, Button,
  Tabs, Tab, Chip, useTheme as useMuiTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Image as ImageIcon,
  AttachFile as DocIcon,
  Chat as AllIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  ExitToApp as LeaveIcon,
  DeleteForever as DeleteGroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useChat } from '../../contexts/ChatContext';
import { Conversation, ChatMessage } from '../../types/chat.types';
import { useAppSelector } from '../../hooks/redux.hooks';
import { useLanguage } from '../../contexts/LanguageContext';
import { chatTranslations } from './chatTranslations';
import MessageInput from './MessageInput';
// GroupManageDialog is rendered by ChatWidget (outside the overflow:hidden Paper)

import config from '../../config';
const API_ORIGIN = config.API_ORIGIN;

type FilterTab = 'all' | 'media' | 'docs';

// Deterministic avatar gradient
const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];
const avatarGradient = (name: string) =>
  AVATAR_COLORS[(name.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

interface Props {
  conversation: Conversation;
  onOpenGroupSettings?: () => void;
}

const MessageList: React.FC<Props> = ({ conversation, onOpenGroupSettings }) => {
  const { messages, deleteMessage, leaveGroup, deleteGroup, readReceipts } = useChat();
  const currentUserId = useAppSelector(s => s.auth.user?.userId ?? 0);
  const isSuperAdmin  = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
  const { language } = useLanguage();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = chatTranslations[language];
  const isRTL = language === 'ar';

  const convMessages  = messages[conversation.conversationId] ?? [];
  const bottomRef     = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl]         = useState<null | HTMLElement>(null);
  const [selectedMsg, setSelectedMsg]   = useState<ChatMessage | null>(null);
  const [lightboxUrl, setLightboxUrl]   = useState<string | null>(null);
  const [lightboxName, setLightboxName] = useState<string>('');
  const [filterTab, setFilterTab]       = useState<FilterTab>('all');
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const myMember     = conversation.members.find(m => m.userId === currentUserId);
  const isGroupAdmin = myMember?.role === 'Admin' || isSuperAdmin;
  const onlineCount  = conversation.members.filter(m => m.isOnline).length;

  const filteredMessages = convMessages.filter(msg => {
    if (filterTab === 'all')   return true;
    if (filterTab === 'media') return !!msg.attachmentContentType?.startsWith('image/');
    if (filterTab === 'docs')  return !!msg.attachmentPath && !msg.attachmentContentType?.startsWith('image/');
    return true;
  });

  const mediaCount = convMessages.filter(m => !m.isDeleted && m.attachmentContentType?.startsWith('image/')).length;
  const docsCount  = convMessages.filter(m => !m.isDeleted && m.attachmentPath && !m.attachmentContentType?.startsWith('image/')).length;

  useEffect(() => {
    if (filterTab === 'all') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [convMessages.length, filterTab]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, msg: ChatMessage) => {
    e.preventDefault();
    setAnchorEl(e.currentTarget);
    setSelectedMsg(msg);
  };

  const handleDelete = async () => {
    if (!selectedMsg) return;
    await deleteMessage(selectedMsg.messageId, selectedMsg.conversationId);
    setAnchorEl(null);
    setSelectedMsg(null);
  };

  const openLightbox = (url: string, name: string) => {
    setLightboxUrl(url);
    setLightboxName(name);
  };

  const canDelete = selectedMsg && (selectedMsg.senderId === currentUserId || isSuperAdmin);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{
        px: 2, py: 1.2,
        background: isDark
          ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 100%)'
          : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        display: 'flex', alignItems: 'center', gap: 1.25,
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          bgcolor: 'rgba(255,255,255,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {conversation.isGroup
            ? <GroupIcon sx={{ color: 'white', fontSize: 18 }} />
            : <PersonIcon sx={{ color: 'white', fontSize: 18 }} />
          }
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white', fontSize: '0.88rem', lineHeight: 1.2 }} noWrap>
            {conversation.name}
          </Typography>
          {conversation.isGroup && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.68rem' }}>
              {onlineCount} {t.online} · {conversation.members.length} {t.members}
            </Typography>
          )}
        </Box>

        {conversation.isGroup && (
          <>
            {isGroupAdmin && (
              <Tooltip title={t.groupSettings}>
                <IconButton size="small" onClick={() => onOpenGroupSettings?.()}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                  <SettingsIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t.moreOptions}>
              <IconButton size="small" onClick={(e) => setGroupMenuAnchor(e.currentTarget)}
                sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                <MoreVertIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={groupMenuAnchor}
              open={Boolean(groupMenuAnchor)}
              onClose={() => setGroupMenuAnchor(null)}
              sx={{ zIndex: 1600 }}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
            >
              <MenuItem onClick={() => { setGroupMenuAnchor(null); setConfirmLeave(true); }}>
                <LeaveIcon sx={{ mr: 1, fontSize: 18 }} /> {t.leaveGroup}
              </MenuItem>
              {isGroupAdmin && (
                <MenuItem onClick={() => { setGroupMenuAnchor(null); setConfirmDelete(true); }}
                  sx={{ color: 'error.main' }}>
                  <DeleteGroupIcon sx={{ mr: 1, fontSize: 18 }} /> {t.deleteGroup}
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {/* Leave Group Confirmation */}
      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)} sx={{ zIndex: 1600 }}>
        <DialogTitle>{t.leaveGroup}</DialogTitle>
        <DialogContent><Typography>{t.leaveGroupConfirm}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>{t.cancel}</Button>
          <Button color="warning" variant="contained" onClick={async () => { setConfirmLeave(false); await leaveGroup(conversation.conversationId); }}>
            {t.leaveGroup}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} sx={{ zIndex: 1600 }}>
        <DialogTitle>{t.deleteGroup}</DialogTitle>
        <DialogContent><Typography>{t.deleteGroupConfirm}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>{t.cancel}</Button>
          <Button color="error" variant="contained" onClick={async () => { setConfirmDelete(false); await deleteGroup(conversation.conversationId); }}>
            {t.deleteGroup}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Filter Tabs ── */}
      <Box sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={filterTab}
          onChange={(_, v) => setFilterTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 38,
            '& .MuiTab-root': { minHeight: 38, fontSize: '0.74rem', py: 0, fontWeight: 600 },
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0' },
          }}
        >
          <Tab
            value="all"
            icon={<AllIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label={t.all}
          />
          <Tab
            value="media"
            icon={<ImageIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t.media}
                {mediaCount > 0 && (
                  <Chip label={mediaCount} size="small" color="primary"
                    sx={{ height: 15, fontSize: '0.58rem', '& .MuiChip-label': { px: 0.5 } }} />
                )}
              </Box>
            }
          />
          <Tab
            value="docs"
            icon={<DocIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t.docs}
                {docsCount > 0 && (
                  <Chip label={docsCount} size="small" color="secondary"
                    sx={{ height: 15, fontSize: '0.58rem', '& .MuiChip-label': { px: 0.5 } }} />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* ── Messages Area ── */}
      <Box sx={{
        flex: 1, overflowY: 'auto', p: filterTab === 'all' ? 1.5 : 1,
        display: filterTab === 'media' ? 'grid' : 'flex',
        gridTemplateColumns: 'repeat(3, 1fr)',
        flexDirection: 'column',
        gap: filterTab === 'media' ? 0.75 : 0.5,
        alignContent: 'flex-start',
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
      }}>
        {filteredMessages.length === 0 ? (
          <Box sx={{
            gridColumn: '1 / -1',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', color: 'text.disabled', py: 5, gap: 1.5,
          }}>
            <Box sx={{ fontSize: 38, opacity: 0.4 }}>
              {filterTab === 'all' ? '💬' : filterTab === 'media' ? '🖼️' : '📄'}
            </Box>
            <Typography variant="caption">
              {filterTab === 'all' ? t.emptyAll : filterTab === 'media' ? t.emptyMedia : t.emptyDocs}
            </Typography>
          </Box>
        ) : filterTab === 'media' ? (
          filteredMessages.map(msg => (
            <MediaThumbnail key={msg.messageId} msg={msg} onOpen={openLightbox} downloadLabel={t.download} />
          ))
        ) : filterTab === 'docs' ? (
          filteredMessages.map(msg => (
            <DocRow key={msg.messageId} msg={msg} downloadLabel={t.download} />
          ))
        ) : (
          filteredMessages.map(msg => (
            <MessageBubble
              key={msg.messageId}
              msg={msg}
              isGroup={conversation.isGroup}
              currentUserId={currentUserId}
              isRTL={isRTL}
              deletedLabel={t.messageDeleted}
              readReceipts={readReceipts}
              onContextMenu={(e) => openMenu(e, msg)}
              onImageClick={openLightbox}
              zoomLabel={t.viewFullSize}
              downloadLabel={t.download}
            />
          ))
        )}
        {filterTab === 'all' && <div ref={bottomRef} />}
      </Box>

      {/* ── Input ── */}
      {filterTab === 'all' && (
        <MessageInput conversationId={conversation.conversationId} />
      )}

      {/* ── Context Menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 160, boxShadow: 3 } }}
      >
        {canDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main', fontSize: '0.85rem', gap: 1 }}>
            <DeleteIcon fontSize="small" />
            {t.deleteMessage}
          </MenuItem>
        )}
        {selectedMsg?.attachmentPath && (
          <MenuItem
            component="a"
            href={`${API_ORIGIN}/${selectedMsg.attachmentPath}`}
            download={selectedMsg.attachmentOriginalName ?? 'file'}
            target="_blank"
            rel="noreferrer"
            onClick={() => setAnchorEl(null)}
            sx={{ fontSize: '0.85rem', gap: 1 }}
          >
            <DownloadIcon fontSize="small" />
            {t.downloadFile}
          </MenuItem>
        )}
      </Menu>

      {/* ── Image Lightbox ── */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', m: 1, borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip title={t.close}>
            <IconButton onClick={() => setLightboxUrl(null)}
              sx={{ position: 'absolute', top: 10, right: 10, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', zIndex: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.download}>
            <IconButton
              component="a" href={lightboxUrl ?? ''} download={lightboxName || 'image'} target="_blank" rel="noreferrer"
              sx={{ position: 'absolute', top: 10, right: 58, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', zIndex: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt={lightboxName}
              style={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block', borderRadius: 6 }}
            />
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
};

// ── Message Bubble ───────────────────────────────────────────────────────────

interface BubbleProps {
  msg: ChatMessage;
  isGroup: boolean;
  currentUserId: number;
  isRTL: boolean;
  deletedLabel: string;
  zoomLabel: string;
  downloadLabel: string;
  readReceipts: Record<number, { userId: number; readAt: string }[]>;
  onContextMenu: (e: React.MouseEvent<HTMLElement>) => void;
  onImageClick: (url: string, name: string) => void;
}

const AVATAR_COLORS_B = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];
const avGrad = (name: string) =>
  AVATAR_COLORS_B[(name.charCodeAt(0) ?? 0) % AVATAR_COLORS_B.length];

const MessageBubble: React.FC<BubbleProps> = ({
  msg, isGroup, currentUserId, isRTL, deletedLabel, zoomLabel, downloadLabel, readReceipts,
  onContextMenu, onImageClick,
}) => {
  const muiTheme = useMuiTheme();
  const isDark   = muiTheme.palette.mode === 'dark';
  const isOwn    = msg.senderId === currentUserId;
  const time     = format(new Date(msg.sentAt), 'HH:mm');

  if (msg.isDeleted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
        <Typography variant="caption" sx={{
          fontStyle: 'italic', color: 'text.disabled',
          bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 2,
        }}>
          🚫 {deletedLabel} · {time}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        alignItems: 'flex-end', gap: 0.75,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
      onContextMenu={onContextMenu}
    >
      {!isOwn && isGroup && (
        <Avatar sx={{
          width: 24, height: 24, fontSize: 9, mb: 0.5, flexShrink: 0,
          background: avGrad(msg.senderName), fontWeight: 700,
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        }}>
          {msg.senderName.charAt(0).toUpperCase()}
        </Avatar>
      )}

      <Box sx={{
        maxWidth: '72%',
        background: isOwn
          ? 'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)'
          : isDark ? muiTheme.palette.grey[800] : '#ffffff',
        color: isOwn ? 'white' : muiTheme.palette.text.primary,
        borderRadius: isOwn
          ? (isRTL ? '16px 4px 16px 16px' : '16px 16px 4px 16px')
          : (isRTL ? '4px 16px 16px 16px' : '16px 16px 16px 4px'),
        px: 1.5, py: 0.85,
        cursor: 'context-menu',
        boxShadow: isOwn
          ? '0 2px 12px rgba(79,172,254,0.35)'
          : isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.08)',
        border: isOwn ? 'none' : isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
        position: 'relative',
      }}>
        {isGroup && !isOwn && (
          <Typography variant="caption" sx={{
            fontWeight: 700, display: 'block', mb: 0.4,
            fontSize: '0.7rem',
            background: avGrad(msg.senderName),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {msg.senderName}
          </Typography>
        )}

        {msg.attachmentPath && (
          <AttachmentPreview
            msg={msg}
            isOwn={isOwn}
            zoomLabel={zoomLabel}
            downloadLabel={downloadLabel}
            onImageClick={onImageClick}
          />
        )}

        {msg.content && (
          <Typography variant="body2" sx={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontSize: '0.875rem', lineHeight: 1.45,
            direction: isRTL ? 'rtl' : 'ltr',
          }}>
            {msg.content}
          </Typography>
        )}

        <Typography variant="caption" sx={{
          display: 'flex', alignItems: 'center', justifyContent: isRTL ? 'flex-start' : 'flex-end',
          gap: 0.4,
          opacity: 0.7, mt: 0.3, fontSize: '0.6rem',
          color: isOwn ? 'rgba(255,255,255,0.85)' : 'text.disabled',
        }}>
          {time}
          {isOwn && (
            <span style={{ fontSize: '0.7rem', marginLeft: 2 }}>
              {readReceipts[msg.conversationId]?.some(r =>
                r.userId !== currentUserId && new Date(r.readAt) >= new Date(msg.sentAt)
              ) ? '✓✓' : '✓'}
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Attachment Preview ───────────────────────────────────────────────────────

interface AttachProps {
  msg: ChatMessage;
  isOwn: boolean;
  zoomLabel: string;
  downloadLabel: string;
  onImageClick: (url: string, name: string) => void;
}

const AttachmentPreview: React.FC<AttachProps> = ({ msg, isOwn, zoomLabel, downloadLabel, onImageClick }) => {
  const muiTheme = useMuiTheme();
  const isDark   = muiTheme.palette.mode === 'dark';
  const isImage  = msg.attachmentContentType?.startsWith('image/');
  const url      = `${API_ORIGIN}/${msg.attachmentPath}`;
  const sizeKb   = msg.attachmentSize ? `${(msg.attachmentSize / 1024).toFixed(0)} KB` : '';

  if (isImage) {
    return (
      <Box sx={{ mb: msg.content ? 0.75 : 0, position: 'relative', display: 'inline-block' }}>
        <Box
          component="img"
          src={url}
          alt={msg.attachmentOriginalName ?? 'image'}
          onClick={() => onImageClick(url, msg.attachmentOriginalName ?? 'image')}
          sx={{
            maxWidth: '100%', maxHeight: 200, borderRadius: 1.5,
            display: 'block', cursor: 'zoom-in',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        />
        <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
          <Tooltip title={zoomLabel}>
            <IconButton size="small"
              onClick={() => onImageClick(url, msg.attachmentOriginalName ?? 'image')}
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.35, '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}>
              <ZoomInIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={downloadLabel}>
            <IconButton size="small" component="a" href={url}
              download={msg.attachmentOriginalName ?? 'image'} target="_blank" rel="noreferrer"
              sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.35, '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}>
              <DownloadIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // File
  const ext = (msg.attachmentOriginalName ?? '').split('.').pop()?.toUpperCase() ?? 'FILE';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      mb: msg.content ? 0.75 : 0, p: 1, borderRadius: 1.5,
      bgcolor: isOwn ? 'rgba(255,255,255,0.18)' : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(79,172,254,0.08)',
      border: isOwn ? '1px solid rgba(255,255,255,0.25)' : isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(79,172,254,0.2)',
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1, flexShrink: 0,
        bgcolor: isOwn ? 'rgba(255,255,255,0.25)' : 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.55rem', fontWeight: 800, color: 'white',
      }}>
        {ext}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: isOwn ? 'white' : 'text.primary' }} noWrap>
          {msg.attachmentOriginalName}
        </Typography>
        {sizeKb && (
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isOwn ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>
            {sizeKb}
          </Typography>
        )}
      </Box>
      <Tooltip title={downloadLabel}>
        <IconButton size="small" component="a" href={url}
          download={msg.attachmentOriginalName ?? 'file'} target="_blank" rel="noreferrer"
          sx={{ color: isOwn ? 'white' : 'primary.main', p: 0.25, flexShrink: 0 }}>
          <DownloadIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ── Media Thumbnail ──────────────────────────────────────────────────────────

const MediaThumbnail: React.FC<{ msg: ChatMessage; downloadLabel: string; onOpen: (url: string, name: string) => void }> = ({ msg, downloadLabel, onOpen }) => {
  const url  = `${API_ORIGIN}/${msg.attachmentPath}`;
  const name = msg.attachmentOriginalName ?? 'image';
  const time = format(new Date(msg.sentAt), 'dd/MM HH:mm');

  return (
    <Box
      sx={{
        position: 'relative', aspectRatio: '1', overflow: 'hidden',
        borderRadius: 1.5, cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover img': { transform: 'scale(1.05)' },
        '&:hover .overlay': { opacity: 1 },
      }}
      onClick={() => onOpen(url, name)}
    >
      <Box
        component="img"
        src={url}
        alt={name}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.25s' }}
      />
      {/* Hover overlay */}
      <Box className="overlay" sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)',
        opacity: 0, transition: 'opacity 0.2s',
      }} />
      <Tooltip title={downloadLabel}>
        <IconButton size="small" component="a" href={url}
          download={name} target="_blank" rel="noreferrer"
          onClick={e => e.stopPropagation()}
          sx={{
            position: 'absolute', bottom: 5, right: 5,
            bgcolor: 'rgba(0,0,0,0.55)', color: 'white', p: 0.35,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}>
          <DownloadIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
      <Typography variant="caption" sx={{
        position: 'absolute', bottom: 5, left: 6,
        color: 'white', fontSize: '0.58rem',
        textShadow: '0 1px 4px rgba(0,0,0,0.8)',
      }}>
        {time}
      </Typography>
    </Box>
  );
};

// ── Doc Row ──────────────────────────────────────────────────────────────────

const DocRow: React.FC<{ msg: ChatMessage; downloadLabel: string }> = ({ msg, downloadLabel }) => {
  const url    = `${API_ORIGIN}/${msg.attachmentPath}`;
  const sizeKb = msg.attachmentSize ? `${(msg.attachmentSize / 1024).toFixed(0)} KB` : '';
  const time   = format(new Date(msg.sentAt), 'dd/MM/yyyy HH:mm');
  const ext    = (msg.attachmentOriginalName ?? '').split('.').pop()?.toUpperCase() ?? 'FILE';

  const extColor: Record<string, string> = {
    PDF: '#e53935', DOCX: '#1565c0', DOC: '#1565c0',
    XLSX: '#2e7d32', XLS: '#2e7d32', CSV: '#2e7d32',
    TXT: '#546e7a',
  };
  const color = extColor[ext] ?? '#6e6e6e';

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 1.5, py: 1, borderRadius: 2,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.1)' },
    }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
        bgcolor: color + '15',
        border: `1.5px solid ${color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.58rem', fontWeight: 800, color,
      }}>
        {ext}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem' }}>
          {msg.attachmentOriginalName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {sizeKb}{sizeKb && ' · '}{msg.senderName} · {time}
        </Typography>
      </Box>

      <Tooltip title={downloadLabel}>
        <IconButton size="small" component="a" href={url}
          download={msg.attachmentOriginalName ?? 'file'} target="_blank" rel="noreferrer"
          sx={{
            background: 'linear-gradient(135deg,#4facfe,#00f2fe)',
            color: 'white', width: 30, height: 30,
            '&:hover': { transform: 'scale(1.08)' },
            transition: 'transform 0.15s',
          }}>
          <DownloadIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MessageList;
